import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle } from './shared';

export function LogsContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">日志</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          Gateway 网关有两个日志"界面"：控制台输出（终端/Debug UI 中看到的内容）和文件日志（JSON 行）由 Gateway 网关日志记录器写入。
        </p>
      </div>

      <SectionTitle id="file-logger">基于文件的日志记录器</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <p>
          默认滚动日志文件位于 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">/tmp/openclaw/</code> 下（每天一个文件）：
        </p>
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <code className="text-cyan-400 font-mono">openclaw-YYYY-MM-DD.log</code>
        </div>
        <p>日期使用 Gateway 网关主机的本地时区。</p>
        <p>
          配置项：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">logging.file</code>、
          <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400 ml-1">logging.level</code>
        </p>
        <p>文件格式是每行一个 JSON 对象。</p>
      </div>

      <SubsectionTitle id="follow-logs">实时跟踪日志</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`openclaw logs --follow`}
      />

      <SectionTitle id="verbose-vs-level">Verbose 与日志级别</SectionTitle>
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5 space-y-3">
          <div className="flex items-start gap-3">
            <i className="ri-file-text-line text-green-400 text-lg mt-0.5 flex-shrink-0"></i>
            <p className="text-slate-300 text-sm">
              文件日志完全由 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">logging.level</code> 控制。
            </p>
          </div>
          <div className="flex items-start gap-3">
            <i className="ri-terminal-line text-yellow-400 text-lg mt-0.5 flex-shrink-0"></i>
            <p className="text-slate-300 text-sm">
              <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">--verbose</code> 仅影响控制台详细程度（和 WS 日志样式）；不会提高文件日志级别。
            </p>
          </div>
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-cyan-400 text-lg mt-0.5 flex-shrink-0"></i>
            <p className="text-slate-300 text-sm">
              要在文件日志中捕获仅 verbose 的详细信息，将 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">logging.level</code> 设置为 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">debug</code> 或 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">trace</code>。
            </p>
          </div>
        </div>
      </div>

      <SectionTitle id="console-capture">控制台捕获</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <p>
          CLI 捕获 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">console.log/info/warn/error/debug/trace</code> 并写入文件日志。
        </p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
            <p className="text-slate-400 text-xs mb-1">配置项</p>
            <code className="text-cyan-400 font-mono text-sm">logging.consoleLevel</code>
            <p className="text-slate-400 text-xs mt-2">默认：<code className="text-green-400">info</code></p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
            <p className="text-slate-400 text-xs mb-1">配置项</p>
            <code className="text-cyan-400 font-mono text-sm">logging.consoleStyle</code>
            <p className="text-slate-400 text-xs mt-2">
              可选值：<code className="text-green-400">pretty</code> | <code className="text-green-400">compact</code> | <code className="text-green-400">json</code>
            </p>
          </div>
        </div>
      </div>

      <SectionTitle id="redact">工具摘要脱敏</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 space-y-2">
          <div>
            <code className="text-cyan-400 font-mono text-sm">logging.redactSensitive</code>
            <span className="text-slate-400 text-sm ml-2">：</span>
            <code className="text-green-400 text-sm ml-1">off</code>
            <span className="text-slate-500 text-sm mx-1">|</span>
            <code className="text-green-400 text-sm">tools</code>
            <span className="text-slate-500 text-sm ml-2">（默认：<code className="text-green-400">tools</code>）</span>
          </div>
          <div>
            <code className="text-cyan-400 font-mono text-sm">logging.redactPatterns</code>
            <span className="text-slate-400 text-sm ml-2">：正则表达式字符串数组</span>
          </div>
        </div>
      </div>

      <SectionTitle id="ws-logs">Gateway 网关 WebSocket 日志</SectionTitle>
      <div className="space-y-3">
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-eye-off-line text-slate-400 text-lg mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-white text-sm font-medium mb-1">普通模式（无 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">--verbose</code>）</p>
            <p className="text-slate-400 text-sm">仅打印"有趣的"RPC 结果（错误、慢调用、解析错误）</p>
          </div>
        </div>
        <div className="flex items-start gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <i className="ri-eye-line text-green-400 text-lg mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-white text-sm font-medium mb-1">详细模式（<code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">--verbose</code>）</p>
            <p className="text-slate-400 text-sm">打印所有 WS 请求/响应流量</p>
          </div>
        </div>
      </div>

      <SectionTitle id="ws-log-style">WS 日志样式</SectionTitle>
      <div className="space-y-3">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <code className="text-cyan-400 font-mono text-sm">--ws-log auto</code>
          <span className="text-slate-500 text-sm ml-2">（默认）</span>
        </div>
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <code className="text-cyan-400 font-mono text-sm">--ws-log compact</code>
          <p className="text-slate-400 text-sm mt-1">详细时使用紧凑输出（配对的请求/响应）</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <code className="text-cyan-400 font-mono text-sm">--ws-log full</code>
          <p className="text-slate-400 text-sm mt-1">详细时使用完整的每帧输出</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <code className="text-cyan-400 font-mono text-sm">--compact</code>
          <p className="text-slate-400 text-sm mt-1"><code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">--ws-log compact</code> 的别名</p>
        </div>
      </div>

      <SubsectionTitle id="ws-log-examples">示例命令</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 优化的（仅错误/慢调用）
openclaw gateway

# 显示所有 WS 流量（配对）
openclaw gateway --verbose --ws-log compact

# 显示所有 WS 流量（完整元数据）
openclaw gateway --verbose --ws-log full`}
      />

      <SectionTitle id="console-format">控制台格式化（子系统日志）</SectionTitle>
      <div className="space-y-3 text-slate-300 leading-relaxed">
        <p>TTY 感知，打印一致的带前缀行。</p>
        <ul className="space-y-2 ml-4">
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
            <span>每行都有子系统前缀（例如 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">[gateway]</code>、<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">[canvas]</code>、<code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">[tailscale]</code>）</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
            <span>子系统颜色（每个子系统稳定）加上级别着色</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
            <span>缩短的子系统前缀：删除前导 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">gateway/</code> + <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">channels/</code>，保留最后 2 个段</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
            <span><code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">logRaw()</code> 用于 QR/UX 输出（无前缀，无格式化）</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-arrow-right-s-line text-green-400 mt-0.5 flex-shrink-0"></i>
            <span>WhatsApp 消息正文以 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">debug</code> 级别记录（使用 <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">--verbose</code> 查看）</span>
          </li>
        </ul>
      </div>

      <InfoBox type="info" title="提示">
        要在文件日志中捕获完整的调试信息，请将 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">logging.level</code> 设置为 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">debug</code> 或 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">trace</code>，而不是仅使用 <code className="px-1 py-0.5 bg-slate-800 rounded text-cyan-400">--verbose</code>。
      </InfoBox>
    </div>
  );
}

export default LogsContent;
